import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Timeline,
  Tag,
  Button,
  Typography,
  Space,
  Progress,
  Avatar,
  Badge,
  Alert,
  Table,
  message,
  Spin,
} from "antd";
import {
  VideoCameraOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  EyeOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { dashboardService, authService } from "../../../services";
import { all_routes } from "../../../routes/all_routes";

const { Title, Text } = Typography;

const UserDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    totalViews: 0,
    avgRating: 0,
  });
  const [recentTestimonials, setRecentTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  const authData = authService.getCurrentUser();

  const getTitleFromVideoUrl = (videoUrl) => {
    if (!videoUrl) return "Untitled Testimonial";

    try {
      const filename = videoUrl.split("/").pop();

      const nameWithoutExt = filename.split(".")[0];

      const cleanName = nameWithoutExt.replace(/^video-/, "");

      const timestampMatch = cleanName.match(/\d+/);
      if (timestampMatch && timestampMatch[0]) {
        const timestamp = parseInt(timestampMatch[0]);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp);
          return `Testimonial from ${date.toLocaleDateString()}`;
        }
      }

      return cleanName || "Testimonial Video";
    } catch (error) {
      console.error("Error parsing video URL:", error);
      return "Testimonial Video";
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setApiError(false);

      const dashboardResponse = await dashboardService.getUserDashboardStats();

      if (dashboardResponse.status) {
        const dashboardData = dashboardResponse.data;

        setStats({
          total: dashboardData.testimonials?.total || 0,
          approved: dashboardData.testimonials?.approved || 0,
          pending: dashboardData.testimonials?.pending || 0,
          rejected: dashboardData.testimonials?.rejected || 0,
          totalViews: dashboardData.totalViews || 0,
          avgRating: dashboardData.avgRating || 0,
        });

        const transformedTestimonials = (
          dashboardData.recentTestimonials || []
        ).map((testimonial) => ({
          id: testimonial.id,
          title: getTitleFromVideoUrl(testimonial.videoUrl),
          status: testimonial.status,
          date: new Date(testimonial.createdAt).toLocaleDateString(),
          duration: testimonial.videoDuration
            ? formatDuration(testimonial.videoDuration)
            : "0:00",
          rating: testimonial.rating || 0,
          views: testimonial.views || 0,
          feedback: testimonial.reviewNotes || null,
          createdAt: testimonial.createdAt,
        }));

        setRecentTestimonials(transformedTestimonials);
      } else {
        throw new Error(
          dashboardResponse.message || "Failed to load dashboard data"
        );
      }
    } catch (error) {
      console.error("Load user dashboard error:", error);
      setApiError(true);
      message.error("Failed to load dashboard data. Please try again.");

      setStats({
        total: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        totalViews: 0,
        avgRating: 0,
      });
      setRecentTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const getStatusTag = (status) => {
    const statusConfig = {
      approved: {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Approved",
      },
      pending: {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "Pending Review",
      },
      rejected: {
        color: "red",
        icon: <CloseCircleOutlined />,
        text: "Rejected",
      },
    };
    const config = statusConfig[status];
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: "#52c41a",
      pending: "#faad14",
      rejected: "#ff4d4f",
    };
    return colors[status] || "#d9d9d9";
  };

  const columns = [
    {
      title: "Video Created On",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <Space>
          <Avatar
            size="small"
            style={{ backgroundColor: getStatusColor(record.status) }}
            icon={<VideoCameraOutlined />}
          />
          <div>
            <Text strong>{title}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.duration}
              {/* {record.duration} • {record.date} */}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      render: (rating) => (
        <Space>
          {Array.from({ length: 5 }, (_, i) => (
            <span key={i} style={{ color: i < rating ? "#fadb14" : "#d9d9d9" }}>
              ⭐
            </span>
          ))}
        </Space>
      ),
    },
    {
      title: "Views",
      dataIndex: "views",
      key: "views",
      render: (views) => (
        <Badge
          count={views}
          showZero
          style={{ backgroundColor: views > 0 ? "#52c41a" : "#d9d9d9" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewTestimonial(record)}
          >
            View
          </Button>
          {record.status === "rejected" && (
            <Button
              type="link"
              icon={<PlusOutlined />}
              size="small"
              onClick={() => handleRetryTestimonial(record)}
            >
              Retry
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleViewTestimonial = (testimonial) => {
    navigate(all_routes.userRecord, {
      state: {
        autoSwitchToTestimonials: true,
        testimonialId: testimonial.id,
      },
    });
  };

  const handleRetryTestimonial = (testimonial) => {
    navigate(all_routes.userRecord);
    message.info("Redirecting to record a new testimonial");
  };

  const handleCreateNew = () => {
    navigate(all_routes.userRecord);
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const approvalRate = stats.total
    ? Math.round((stats.approved / stats.total) * 100)
    : 0;

  if (loading) {
    return (
      <div className="p-6">
        <Title level={2}>My Dashboard</Title>
        <div className="text-center py-12">
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading your dashboard data...</Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2}>My Dashboard</Title>
          <Text type="secondary">
            Welcome back! Here's your testimonial performance overview.
          </Text>
        </div>
        <Space>
          {apiError && <Button onClick={handleRefresh}>Retry</Button>}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleCreateNew}
          >
            Create New Testimonial
          </Button>
        </Space>
      </div>

      {apiError && (
        <Alert
          message="Connection Issue"
          description="We're having trouble loading your data. Please check your connection and try again."
          type="warning"
          showIcon
          className="mb-6"
        />
      )}

      {/* Main Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="text-center shadow-sm hover:shadow-md transition-shadow duration-300">
            <Statistic
              title="Total Testimonials"
              value={stats.total}
              prefix={<VideoCameraOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="text-center shadow-sm hover:shadow-md transition-shadow duration-300">
            <Statistic
              title="Approved"
              value={stats.approved}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
            <Progress percent={approvalRate} size="small" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="text-center shadow-sm hover:shadow-md transition-shadow duration-300">
            <Statistic
              title="Pending Review"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
            <Text type="secondary">Awaiting approval</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="text-center shadow-sm hover:shadow-md transition-shadow duration-300">
            <Statistic
              title="Average Rating"
              value={stats.avgRating}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: "#fadb14" }}
              suffix="/ 5"
              precision={1}
            />
            <Text type="secondary">Across all testimonials</Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title="My Testimonials"
            className="shadow-sm"
            extra={
              <Button
                type="link"
                onClick={() =>
                  navigate(all_routes.userRecord, {
                    state: { autoSwitchToTestimonials: true },
                  })
                }
              >
                View All
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={recentTestimonials}
              pagination={false}
              size="middle"
              scroll={{ x: 800 }}
              rowKey="id"
              locale={{
                emptyText: "No testimonials found. Create your first one!",
              }}
            />
          </Card>
        </Col>

        {/* Sidebar - Quick Stats & Actions */}
        <Col xs={24} lg={8}>
          {/* Quick Actions */}
          <Card title="Quick Actions" className="shadow-sm mb-4">
            <Space direction="vertical" className="w-full">
              <Button
                type="primary"
                block
                icon={<PlusOutlined />}
                onClick={handleCreateNew}
                size="large"
              >
                Record New Testimonial
              </Button>
            </Space>
          </Card>

          {/* Performance Stats */}
          <Card title="Performance Overview" className="shadow-sm mb-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <Text strong>Total Views</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    All your testimonials
                  </Text>
                </div>
                <Statistic
                  value={stats.totalViews}
                  valueStyle={{ color: "#1890ff", fontSize: "18px" }}
                />
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text strong>Approval Rate</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Success rate
                  </Text>
                </div>
                <Statistic
                  value={approvalRate}
                  suffix="%"
                  valueStyle={{ color: "#52c41a", fontSize: "18px" }}
                />
              </div>
            </div>
          </Card>

          {/* Recent Activity */}
          <Card title="Recent Activity" className="shadow-sm">
            <Timeline>
              {recentTestimonials.slice(0, 3).map((testimonial) => (
                <Timeline.Item
                  key={testimonial.id}
                  color={
                    testimonial.status === "approved"
                      ? "green"
                      : testimonial.status === "pending"
                      ? "blue"
                      : "orange"
                  }
                  dot={
                    testimonial.status === "approved" ? (
                      <CheckCircleOutlined />
                    ) : testimonial.status === "pending" ? (
                      <VideoCameraOutlined />
                    ) : (
                      <ClockCircleOutlined />
                    )
                  }
                >
                  <Text strong>
                    {testimonial.status === "approved"
                      ? "Testimonial Approved"
                      : testimonial.status === "pending"
                      ? "New Testimonial Recorded"
                      : "Review In Progress"}
                  </Text>
                  <br />
                  <Text type="secondary">
                    "{testimonial.title}"{" "}
                    {testimonial.status === "approved"
                      ? "is now live"
                      : testimonial.status === "pending"
                      ? "is awaiting review"
                      : "is being reviewed"}
                  </Text>
                </Timeline.Item>
              ))}
              {recentTestimonials.length === 0 && (
                <Timeline.Item>
                  <Text type="secondary">No recent activity</Text>
                </Timeline.Item>
              )}
            </Timeline>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserDashboard;
